import { apiClient, handleApiRequest } from './client'

export interface PatternPromotionResponse {
  id: string
  pattern_regex: string
  pattern_field: string
  pattern_match_mode: string
  pattern_flags: string[]
  pattern_priority: number
  source_school_id: string | null
  source_country_code: string | null
  current_level: string
  stage1_status: string | null
  stage1_score: number | null
  stage2_status: string | null
  stage2_score: number | null
  combined_score: number | null
  status: string
  affected_schools: Array<{
    school_id: string
    school_name: string
    status: string
    score: number | null
    matched_count?: number
    total_tokens?: number
  }> | null
  created_at: string
  updated_at: string
}

export interface PatternPromotionListResponse {
  total: number
  pending: number
  review_queue: number
  promoted: number
  flagged: number
  items: PatternPromotionResponse[]
}

export interface PatternItem {
  regex: string
  field: string
  match_mode: string
  flags: string[]
  priority: number
  source: string
  school_index?: number | null
}

export interface PatternData {
  single: PatternItem[]
  multi: PatternItem[]
  orphan: PatternItem[]
  ignore_tokens: string[]
}

export interface SchoolPatternsResponse {
  single: PatternItem[]
  multi: PatternItem[]
  orphan: PatternItem[]
  ignore_tokens: string[]
  orphan_threshold: number
}

export interface CreatePatternRequest {
  regex: string
  field: string
  match_mode: string
  flags: string[]
  priority: number
}

export interface UpdatePatternRequest {
  regex?: string
  field?: string
  match_mode?: string
  flags?: string[]
  priority?: number
}

export const patternPipelineApi = {
  list: (status?: string, sort?: string) => {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    if (sort) params.set('sort', sort)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return handleApiRequest<PatternPromotionListResponse>(
      apiClient.get(`/api/v1/ultraadmin/patterns/pipeline${qs}`)
    )
  },

  getById: (id: string) =>
    handleApiRequest<PatternPromotionResponse>(
      apiClient.get(`/api/v1/ultraadmin/patterns/pipeline/${id}`)
    ),

  promote: (id: string, targetLevel: 'regional' | 'global', notes?: string) =>
    handleApiRequest<{ status: string; message: string }>(
      apiClient.post(`/api/v1/ultraadmin/patterns/pipeline/${id}/promote`, {
        target_level: targetLevel,
        notes,
      })
    ),

  reject: (id: string, reason: string) =>
    handleApiRequest<{ status: string; message: string }>(
      apiClient.post(`/api/v1/ultraadmin/patterns/pipeline/${id}/reject`, { reason })
    ),

  reAnalyze: (id: string) =>
    handleApiRequest<{ status: string; message: string }>(
      apiClient.post(`/api/v1/ultraadmin/patterns/pipeline/${id}/re-analyze`)
    ),

  getGlobal: () =>
    handleApiRequest<PatternData>(
      apiClient.get('/api/v1/ultraadmin/patterns/global')
    ),

  getRegional: () =>
    handleApiRequest<Record<string, unknown>>(
      apiClient.get('/api/v1/ultraadmin/patterns/regional')
    ),

  getFlagged: () =>
    handleApiRequest<PatternPromotionResponse[]>(
      apiClient.get('/api/v1/ultraadmin/patterns/flagged')
    ),
}

export const schoolPatternApi = {
  getPatterns: () =>
    handleApiRequest<SchoolPatternsResponse>(
      apiClient.get('/api/v1/superadmin/patterns')
    ),

  createSingle: (data: CreatePatternRequest) =>
    handleApiRequest<{ status: string; message: string; promotion_id: string }>(
      apiClient.post('/api/v1/superadmin/patterns/single', data)
    ),

  updateSingle: (index: number, data: UpdatePatternRequest) =>
    handleApiRequest<{ status: string; message: string }>(
      apiClient.put(`/api/v1/superadmin/patterns/single/${index}`, data)
    ),

  deleteSingle: (index: number) =>
    handleApiRequest<{ status: string; message: string }>(
      apiClient.delete(`/api/v1/superadmin/patterns/single/${index}`)
    ),

  updateIgnoreTokens: (tokens: string[]) =>
    handleApiRequest<{ status: string; message: string }>(
      apiClient.put('/api/v1/superadmin/patterns/ignore-tokens', { tokens })
    ),

  updateOrphanThreshold: (threshold: number) =>
    handleApiRequest<{ status: string; message: string }>(
      apiClient.put('/api/v1/superadmin/patterns/orphan-threshold', { threshold })
    ),
}
